/**
 * AvisoDeChamada — a ligação chegando (Task 11, 24/10/2026).
 *
 * O Augusto abriu o pedido porque, do outro lado, "não aparecia nada": nem aviso, nem som. Este é o aviso. O
 * `CallProvider` desenha UM por ligação que está tocando, numa pilha fixa no alto da tela
 * (`pilha-de-avisos-de-chamada`), e toca a campainha enquanto houver algum.
 *
 * O desenho (`docs/superpowers/specs/2026-10-22-ligacoes-do-chat-tela.md`, §1) é o contrato — o que pesa:
 *
 * - **Foco: só se não roubar nada.** Com a pessoa digitando (o composer), o foco FICA onde está: um `Espaço`
 *   ou `Enter` do texto atenderia a ligação, abriria o microfone e derrubaria uma ligação em curso. Nesse
 *   caso quem avisa é o `anuncio-de-chamada` da pilha (leitor de tela). Sem campo focado, o foco vai para
 *   "Atender" e, quando o cartão sai, VOLTA para onde estava (se o elemento ainda existe e a pessoa não foi
 *   para outro).
 * - **`Esc` não recusa.** Recusar é um clique, e nenhum atalho global: dispararia no meio de uma digitação.
 * - **A falha do Atender aparece AQUI, em linha** (`erro-do-aviso`), nunca em toast: no celular o toast do
 *   sonner fica ATRÁS deste cartão (`z-[110]`). `onAtender` rejeita nas falhas que não são o 409 (que o
 *   provider trata e resolve); `onRecusar` sempre resolve.
 * - **O texto em dois nós** ("Juarez Costa" no `h2`, "está ligando · voz" no `p`, com um espaço entre eles):
 *   o `getByText` casa o texto próprio de UM nó, e o teste lê o cartão inteiro com `toHaveTextContent`.
 * - Botões em `grid` (nunca `flex-wrap`), com a própria altura: `h-12` no celular (alvo de toque com a
 *   campainha tocando), `md:h-11` no desktop. O `Button` traz `sm:h-10`, por isso o `sm:h-12`.
 */
import { AlertCircle, Loader2, Phone, PhoneOff, Users, Video } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { CSSProperties } from "react";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import type { ChamadaChegando } from "@/lib/chamadas/eventosDaChamada";
import { focoEmCampoEditavel } from "@/lib/chamadas/focoDoAviso";

export interface AvisoDeChamadaProps {
  chamada: ChamadaChegando;
  /**
   * Resolve quando o `join` terminou (ou quando o 409 já tirou o cartão). REJEITA nas outras falhas (rede,
   * 5xx), sem toast: o cartão reabre os botões e mostra o erro em linha.
   */
  onAtender: () => Promise<void>;
  /** Sempre resolve: em falha o provider tira o cartão mesmo assim (parar de tocar vale mais). */
  onRecusar: () => Promise<void>;
  /** Há outra ligação em curso: atender a encerra. */
  emOutraLigacao?: boolean;
}

type Acao = "nenhuma" | "atendendo" | "recusando";

const TOQUE_MAXIMO_S = 30;

export function AvisoDeChamada({
  chamada,
  onAtender,
  onRecusar,
  emOutraLigacao = false,
}: AvisoDeChamadaProps) {
  const { quem_ligou: quemLigou, midia, room_type: tipoDaSala, sala, segundos_restantes } = chamada;
  const idDoNome = useId();
  const idDaLinha = useId();
  const refDoAtender = useRef<HTMLButtonElement>(null);
  const [acao, setAcao] = useState<Acao>("nenhuma");
  const [erro, setErro] = useState<string | null>(null);
  /** Depois de uma falha o Atender reabre, mas o botão desabilitado perdeu o foco: volta a ele (se der). */
  const refocarAposFalha = useRef(false);

  // O foco entra em "Atender" só se isso não roubar nada, e VOLTA para onde estava quando o cartão sai.
  useEffect(() => {
    if (focoEmCampoEditavel()) return undefined;
    const anterior = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    refDoAtender.current?.focus();
    return () => {
      if (!anterior || anterior === document.body || !anterior.isConnected) return;
      // O cartão acabou de sair, então o foco que estava nele caiu no <body>. Se a pessoa já foi para outro
      // elemento, não se puxa de volta.
      const atual = document.activeElement;
      if (atual === null || atual === document.body) anterior.focus();
    };
  }, []);

  useEffect(() => {
    if (acao !== "nenhuma" || !refocarAposFalha.current) return;
    refocarAposFalha.current = false;
    if (!focoEmCampoEditavel()) refDoAtender.current?.focus();
  }, [acao]);

  const segundos = Number.isFinite(segundos_restantes)
    ? Math.max(0, Math.min(segundos_restantes, TOQUE_MAXIMO_S))
    : TOQUE_MAXIMO_S;

  const atender = () => {
    setErro(null);
    setAcao("atendendo");
    onAtender().catch(() => {
      refocarAposFalha.current = true;
      setAcao("nenhuma");
      setErro("Não foi possível atender. Tente de novo.");
    });
  };

  const recusar = () => {
    setErro(null);
    setAcao("recusando");
    onRecusar().catch(() => setAcao("nenhuma"));
  };

  return (
    <section
      role="alertdialog" // anunciado ao receber foco; não é modal (sem véu, sem prender o foco)
      aria-labelledby={idDoNome}
      aria-describedby={idDaLinha}
      data-testid="aviso-de-chamada"
      data-midia={midia}
      data-call-session-id={chamada.call_session_id}
      // A pilha é um portal do `CallProvider`, FORA da árvore React de qualquer Dialog/Sheet aberto: sem isto o
      // Radix toma o clique aqui por "clicou fora" e fecha o formulário que a pessoa estava preenchendo. O React
      // escuta no contêiner do portal (`document.body`), então parar aqui impede o ouvinte do `document`.
      onPointerDown={(e) => e.stopPropagation()}
      className="pointer-events-auto rounded-2xl border border-border bg-card p-3 text-card-foreground shadow-xl md:p-4 animate-in fade-in-0 zoom-in-95 duration-200 motion-reduce:animate-none"
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-primary/30 animate-ping motion-reduce:hidden"
          />
          <UserAvatar
            userId={quemLigou.id}
            name={quemLigou.nome}
            size={48}
            decorativo
            className="relative"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h2
            id={idDoNome}
            title={quemLigou.nome}
            className="truncate text-base font-semibold leading-tight"
          >
            {quemLigou.nome}
          </h2>{" "}
          <p
            id={idDaLinha}
            className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground"
          >
            {midia === "video" ? (
              <Video className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <Phone className="h-4 w-4 shrink-0" aria-hidden />
            )}
            <span>está ligando · {midia === "video" ? "vídeo" : "voz"}</span>
          </p>
          {/* Grupo: a linha de baixo é o nome do grupo. Sem o nome (link do push), a linha não existe. */}
          {tipoDaSala !== "DM" && sala && (
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate" title={sala}>
                {sala}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* Grid, nunca flex-wrap: a cicatriz do botão que quebrava no desktop. Recusar à esquerda, Atender à
          direita (a ordem do Android). Ícone e texto dizem a mesma coisa que a cor. */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant="destructive"
          data-testid="recusar-chamada"
          disabled={acao !== "nenhuma"}
          className="h-12 text-sm font-semibold [&_svg]:size-5 sm:[&_svg]:size-5 sm:h-12 md:h-11"
          onClick={recusar}
        >
          <PhoneOff aria-hidden /> Recusar
        </Button>
        <Button
          type="button"
          variant="default"
          data-testid="atender-chamada"
          ref={refDoAtender}
          disabled={acao !== "nenhuma"}
          className="h-12 text-sm font-semibold [&_svg]:size-5 sm:[&_svg]:size-5 sm:h-12 md:h-11"
          onClick={atender}
        >
          {acao === "atendendo" ? (
            <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden />
          ) : (
            <Phone aria-hidden />
          )}
          {acao === "atendendo" ? "Entrando…" : "Atender"}
        </Button>
      </div>

      {/* Falha de Atender (rede, 5xx): em linha, dentro do cartão — no celular um toast ficaria atrás dele.
          Texto em `foreground`; só o ícone é vermelho (no escuro o `destructive` é cor de FUNDO). */}
      {erro && (
        <p
          role="alert"
          data-testid="erro-do-aviso"
          className="mt-2 flex items-center justify-center gap-1.5 text-xs font-medium text-foreground"
        >
          <AlertCircle
            className="h-3.5 w-3.5 shrink-0 text-destructive dark:text-red-400"
            aria-hidden
          />
          {erro}
        </p>
      )}
      {emOutraLigacao && (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Atender encerra a ligação atual
        </p>
      )}

      {/* O tempo que falta tocar: a barra esvazia em `segundos_restantes` (máx. 30), só CSS. */}
      <div aria-hidden className="mt-3 h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="toque-esvaziando h-full w-full origin-left bg-primary/60"
          style={{ "--duracao": `${segundos}s` } as CSSProperties}
        />
      </div>
    </section>
  );
}
